// Brochure review loop on a deal's task list (transactions/<txId>/tasks). Pure planning + thin Firebase wrapper.
// MM brochure task (needs a canva.com link) -> agent "Review brochure" -> Approve -> MM "Brochure approved – print"
//                                                                    -> Request changes -> MM "Brochure changes" -> review again ...
// Task keys are deterministic per cycle, so each step creates only one task. History: transactions/<txId>/brochureHistory.
// In-app notifications only (channel 'in-app'); nothing is emailed or texted.
import { ref, runTransaction, push } from 'firebase/database';
import { database } from '../firebaseConfig';
import { createNotification } from './notificationService';

export const isCanvaLink = (url) => {
  try { const u = new URL(String(url || '').trim()); return u.protocol === 'https:' && /(^|\.)canva\.com$/i.test(u.hostname); } catch (e) { return false; }
};
// The original MM brochure task, or an MM "changes" task in the loop. Both need the Brochure link to complete.
export const needsBrochureLink = (task) => !!task && (task.brochureStep === 'changes'
  || (!task.brochureStep && task.role === 'Marketing Manager' && /brochure/i.test(task.name || task.title || '')));
export const isBrochureReview = (task) => !!task && task.brochureStep === 'review';

const day = (now, plus) => { const d = new Date(now); d.setDate(d.getDate() + plus); return d.toISOString().slice(0, 10); };

// action: 'submit' (MM completes with link) | 'approve' | 'changes' (agent, with comment)
export function planBrochureStep(action, { task, address, link, comment = '', actorName = '', now = new Date().toISOString() }) {
  const addr = address || 'this listing';
  const cycle = Number(task.brochureCycle || 0);
  const base = { status: 'pending', shared: false, brochureLink: link, createdAt: now, automatic: false, source: 'brochure-flow' };
  if (action === 'submit') {
    const n = cycle + 1;
    return { key: `brochure_review_${n}`, notifyAgent: true,
      task: { ...base, name: `Review brochure – ${addr}`, role: 'Agent', brochureStep: 'review', brochureCycle: n, dueDate: day(now, 2),
        description: `Review the brochure in Canva, then tap Approve or Request changes.\n${link}` },
      history: { at: now, event: 'submitted', cycle: n, link, by: actorName } };
  }
  if (action === 'approve') {
    return { key: 'brochure_approved',
      task: { ...base, name: `Brochure approved – print ${addr}`, role: 'Marketing Manager', brochureStep: 'print', brochureCycle: cycle, dueDate: day(now, 2),
        description: `Approved by ${actorName || 'the agent'}. Export the print-ready PDF from Canva and upload it to Vistaprint, Wise Pelican, etc.\n${link}` },
      history: { at: now, event: 'approved', cycle, link, by: actorName } };
  }
  if (action === 'changes') {
    return { key: `brochure_changes_${cycle}`,
      task: { ...base, name: `Brochure changes – ${addr}`, role: 'Marketing Manager', brochureStep: 'changes', brochureCycle: cycle, dueDate: day(now, 1),
        agentComments: comment, description: `Changes requested by ${actorName || 'the agent'}:\n${comment}\n\nUpdate the design, paste the Canva link and complete this task to send it back for review.\n${link}` },
      history: { at: now, event: 'changes requested', cycle, link, comment, by: actorName } };
  }
  throw new Error(`Unknown brochure action: ${action}`);
}

// Creates the next task only if it does not exist yet; logs history and notifies the agent only when it was created.
export async function runBrochureStep(txId, ownerUid, action, opts) {
  if (action === 'submit' && !isCanvaLink(opts.link)) throw new Error('Paste the Canva link (https://www.canva.com/...) first.');
  if (action === 'changes' && !String(opts.comment || '').trim()) throw new Error('Add a comment saying what to change.');
  const plan = planBrochureStep(action, opts);
  const res = await runTransaction(ref(database, `transactions/${txId}/tasks/${plan.key}`), (cur) => (cur == null ? plan.task : undefined));
  if (!res.committed) return { created: false, key: plan.key };
  await push(ref(database, `transactions/${txId}/brochureHistory`), plan.history);
  if (plan.notifyAgent && ownerUid) {
    try {
      await createNotification(ownerUid, { type: 'brochure_review', title: 'Brochure ready for review', message: plan.task.name, link: `/transactions/${txId}`, transactionId: txId, channel: 'in-app' });
    } catch (e) { console.error('Could not create in-app alert', e); }
  }
  return { created: true, key: plan.key };
}
